import { Link } from "react-router-dom";
import type { InboxItem, InboxKind, InboxSeverity } from "@/hooks/useInbox";

const kindAction: Record<InboxKind, string> = {
  lead_no_response: "Responder",
  budget_draft: "Enviar",
  budget_stalled: "Lembrar",
  project_overdue: "Resolver",
  project_review_stuck: "Mover",
};

const severityDot: Record<InboxSeverity, string> = {
  high: "bg-bad",
  medium: "bg-warn",
  low: "bg-panel-foreground/30",
};

function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface CockpitQueueProps {
  items: InboxItem[] | undefined;
  isLoading: boolean;
  /** Rows rendered before collapsing the rest into a "ver mais" link. */
  maxItems?: number;
}

/**
 * Dark-panel "Fila de hoje" queue — Direção B (Cockpit) left pane.
 * Fed by the same `useInbox` query the dashboard already loads (passed in
 * as props) so this component never issues its own request.
 */
export function CockpitQueue({ items, isLoading, maxItems = 6 }: CockpitQueueProps) {
  const total = items?.length ?? 0;
  const visible = (items ?? []).slice(0, maxItems);

  return (
    <div className="p-5 md:border-r md:border-panel-foreground/10 md:p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-bold text-panel-foreground">Fila de hoje</h2>
        {total > 0 && (
          <span className="inline-flex h-[22px] items-center rounded-md bg-panel-foreground/[0.12] px-2.5 text-[11px] font-bold text-panel-foreground">
            {total}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-xs text-panel-foreground/50">Carregando…</div>
      ) : visible.length === 0 ? (
        <div className="py-8 text-center text-xs text-panel-foreground/50">
          Fila vazia. Tudo em dia.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {visible.map((item) => (
            <Link
              key={`${item.kind}-${item.ref_id}`}
              to={item.link}
              className="flex items-center gap-3 rounded-xl bg-panel-foreground/[0.06] px-3 py-2.5 transition-colors hover:bg-panel-foreground/[0.12]"
            >
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${severityDot[item.severity]}`}
                aria-hidden="true"
              />
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-panel-foreground/[0.14] text-[11px] font-bold text-panel-foreground">
                {initialsFor(item.title)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-panel-foreground">
                  {item.title}
                </span>
                <span className="block truncate text-[11.5px] text-panel-foreground/55">
                  {item.subtitle}
                </span>
              </span>
              <span className="shrink-0 text-[11.5px] font-semibold text-panel-foreground/80">
                {kindAction[item.kind]}
              </span>
            </Link>
          ))}
          {total > visible.length && (
            <Link
              to="/admin/pipeline"
              className="pt-1 text-center text-[11.5px] text-panel-foreground/55 hover:text-panel-foreground"
            >
              +{total - visible.length} na fila completa
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
